/**
 * WebSocket Service for PortfolioManager
 * Adapted from Finapp_v2 SimplifiedWebSocketService
 *
 * Provides real-time updates for:
 * - Trades
 * - Positions
 * - Compliance breaches
 * - Order status
 * - Trade surveillance alerts
 */

// Define WebSocket connection states
export enum WebSocketStatus {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// Message types that match the backend
export interface WebSocketMessage {
  type: 'subscribe' | 'unsubscribe' | 'ping' | 'pong' | 'data' | 'error' | 'subscription_confirmed' | 'unsubscription_confirmed';
  topic?: 'trades' | 'positions' | 'breaches' | 'orders' | 'alerts';
  message?: string;
  data?: any;
  timestamp?: number;
  token?: string;
}

// Incoming data message structure
export interface DataMessage {
  type: 'data';
  topic: string;
  data: any;
  timestamp: number;
}

// Subscription callback type
type SubscriptionCallback = (data: any) => void;

/**
 * WebSocket Service for real-time portfolio updates
 */
export class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private userId: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private status: WebSocketStatus = WebSocketStatus.DISCONNECTED;
  private heartbeatInterval = 30000; // 30 seconds
  private subscriptions = new Map<string, Set<SubscriptionCallback>>();
  private statusCallbacks = new Set<(status: WebSocketStatus) => void>();
  private messageQueue: WebSocketMessage[] = [];
  private isProcessingQueue = false;

  constructor() {
    // Build WebSocket URL based on current environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = import.meta.env.VITE_WS_HOST || window.location.hostname;
    const port = import.meta.env.VITE_WS_PORT || '8000';
    this.url = `${protocol}//${host}:${port}/api/v1/ws`;

    console.debug('[WebSocket] Service initialized with base URL:', this.url);
  }

  /**
   * Connect to the WebSocket server
   * @param userId User ID for the connection
   * @param token Optional JWT token for authentication
   */
  public connect(userId: number, token?: string): void {
    if (this.ws) {
      console.debug('[WebSocket] Already connected, skipping connect');
      return;
    }

    this.userId = userId;
    const fullUrl = `${this.url}/${userId}`;

    this.updateStatus(WebSocketStatus.CONNECTING);
    console.debug('[WebSocket] Connecting to:', fullUrl);

    try {
      this.ws = new WebSocket(fullUrl);

      // Set up event handlers
      this.ws.onopen = () => this.handleOpen(token);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.handleError(error);
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    console.debug('[WebSocket] Disconnecting WebSocket');
    this.stopHeartbeat();
    this.clearReconnectTimer();

    if (this.ws) {
      // Remove event handlers
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;

      // Close the connection
      this.ws.close();
      this.ws = null;
    }

    this.updateStatus(WebSocketStatus.DISCONNECTED);
    this.subscriptions.clear();
    this.messageQueue = [];
  }

  /**
   * Subscribe to a topic
   * @param topic The topic to subscribe to
   * @param callback Function to call when data is received
   * @param token Optional JWT token for authentication
   */
  public subscribe(
    topic: 'trades' | 'positions' | 'breaches' | 'orders' | 'alerts',
    callback: SubscriptionCallback,
    token?: string
  ): () => void {
    // Store the callback
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic)!.add(callback);

    // Send subscription request if connected
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: 'subscribe',
        topic,
        token
      });
    } else {
      // Queue the subscription for when we connect
      this.messageQueue.push({
        type: 'subscribe',
        topic,
        token
      });
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(topic, callback);
    };
  }

  /**
   * Unsubscribe from a topic
   * @param topic The topic to unsubscribe from
   * @param callback The specific callback to remove
   */
  public unsubscribe(
    topic: 'trades' | 'positions' | 'breaches' | 'orders' | 'alerts',
    callback?: SubscriptionCallback
  ): void {
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      if (callback) {
        callbacks.delete(callback);
      } else {
        callbacks.clear();
      }

      if (callbacks.size === 0) {
        this.subscriptions.delete(topic);

        // Send unsubscribe request if connected
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({
            type: 'unsubscribe',
            topic
          });
        }
      }
    }
  }

  /**
   * Subscribe to status changes
   * @param callback Function to call when status changes
   * @returns Unsubscribe function
   */
  public onStatusChange(callback: (status: WebSocketStatus) => void): () => void {
    this.statusCallbacks.add(callback);
    // Immediately call with current status
    callback(this.status);

    return () => {
      this.statusCallbacks.delete(callback);
    };
  }

  /**
   * Get the current connection status
   */
  public getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.status === WebSocketStatus.CONNECTED;
  }

  // Private methods

  /**
   * Send a message to the WebSocket server
   */
  private send(message: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Cannot send message, socket not open');
      this.messageQueue.push(message);
      return false;
    }

    try {
      const messageString = JSON.stringify(message);
      this.ws.send(messageString);
      console.debug('[WebSocket] Sent message:', message);
      return true;
    } catch (error) {
      console.error('[WebSocket] Error sending message:', error);
      return false;
    }
  }

  /**
   * Update the connection status
   */
  private updateStatus(newStatus: WebSocketStatus): void {
    if (this.status !== newStatus) {
      console.debug(`[WebSocket] Status change: ${this.status} -> ${newStatus}`);
      this.status = newStatus;

      // Notify all status callbacks
      this.statusCallbacks.forEach(callback => {
        try {
          callback(newStatus);
        } catch (error) {
          console.error('[WebSocket] Error in status callback:', error);
        }
      });
    }
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(token?: string): void {
    console.debug('[WebSocket] Connection established');
    this.updateStatus(WebSocketStatus.CONNECTED);
    this.reconnectAttempts = 0;
    this.startHeartbeat();

    // Process queued messages
    this.processMessageQueue();

    // Re-subscribe to all topics
    this.subscriptions.forEach((callbacks, topic) => {
      if (callbacks.size > 0) {
        this.send({
          type: 'subscribe',
          topic: topic as any,
          token
        });
      }
    });
  }

  /**
   * Process queued messages
   */
  private async processMessageQueue(): Promise<void> {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
        // Small delay between messages to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);

      // Only log non-data messages to reduce console spam
      if (message.type !== 'data') {
        console.debug('[WebSocket] Received message:', message);
      }

      // Handle different message types
      switch (message.type) {
        case 'pong':
          // Heartbeat response
          break;

        case 'subscription_confirmed':
          console.debug(`[WebSocket] Subscription confirmed for topic: ${message.topic}`);
          break;

        case 'unsubscription_confirmed':
          console.debug(`[WebSocket] Unsubscription confirmed for topic: ${message.topic}`);
          break;

        case 'data':
          // Route data to appropriate callbacks
          if (message.topic) {
            const callbacks = this.subscriptions.get(message.topic);
            if (callbacks) {
              callbacks.forEach(callback => {
                try {
                  callback(message.data);
                } catch (error) {
                  console.error(`[WebSocket] Error in callback for topic ${message.topic}:`, error);
                }
              });
            }
          }
          break;

        case 'error':
          console.error('[WebSocket] Server error:', message.message);
          break;

        default:
          console.warn('[WebSocket] Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('[WebSocket] Error parsing message:', error, event.data);
    }
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(): void {
    console.debug('[WebSocket] Connection closed');
    this.stopHeartbeat();
    this.ws = null;

    // Don't try to reconnect if we're intentionally disconnected
    if (this.status !== WebSocketStatus.DISCONNECTED) {
      this.tryReconnect();
    } else {
      this.updateStatus(WebSocketStatus.DISCONNECTED);
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(event: any): void {
    const errorMessage = event instanceof Error ? event.message : 'Unknown WebSocket error';
    console.error('[WebSocket] Error:', errorMessage);
    this.updateStatus(WebSocketStatus.ERROR);

    // Attempt to reconnect
    this.tryReconnect();
  }

  /**
   * Try to reconnect to the WebSocket server
   */
  private tryReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`[WebSocket] Max reconnect attempts (${this.maxReconnectAttempts}) reached, giving up`);
      this.updateStatus(WebSocketStatus.DISCONNECTED);
      return;
    }

    if (!this.userId) {
      console.error('[WebSocket] Cannot reconnect without user ID');
      this.updateStatus(WebSocketStatus.DISCONNECTED);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    console.debug(`[WebSocket] Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    this.updateStatus(WebSocketStatus.RECONNECTING);

    this.clearReconnectTimer();

    this.reconnectTimer = setTimeout(() => {
      console.debug('[WebSocket] Reconnecting...');
      if (this.userId) {
        this.connect(this.userId);
      }
    }, delay);
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Start the heartbeat process
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      } else {
        this.stopHeartbeat();
      }
    }, this.heartbeatInterval);
  }

  /**
   * Stop the heartbeat process
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}

// Create and export a singleton instance
let wsServiceInstance: WebSocketService | null = null;

export const getWebSocketService = (): WebSocketService => {
  if (!wsServiceInstance) {
    wsServiceInstance = new WebSocketService();
  }
  return wsServiceInstance;
};

// For testing/reset purposes
export const resetWebSocketService = (): void => {
  if (wsServiceInstance) {
    wsServiceInstance.disconnect();
    wsServiceInstance = null;
  }
};

// Default export
export default getWebSocketService();